import React, { useContext, useState } from "react";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import DataTable from "../../components/Elements/DataTable/DataTable";

import AdminEditUserModal from "../../components/AdminPages/AdminEditUserModal";

import { AuthContext } from "../../context/AuthContext";
import { deleteUser, fetchUsersAdmin } from "../../services/UserService";

import editIcon from "../../assets/data/edit.png";
import deleteIcon from "../../assets/data/delete.png";

// Available roles for user editing modal
const availableRoles = [
  { id: 1, name: "User" },
  { id: 2, name: "Manager" },
  { id: 3, name: "Admin" },
  { id: 4, name: "Tester" },
  { id: 5, name: "Fraud" },
  { id: 6, name: "Blocked" },
];

// Always visible columns in users table
const alwaysVisibleColumns = [
  { key: "email", label: "Email" },
  { key: "username", label: "Username" },
  { key: "role_name", label: "Role" },
  { key: "created_at", label: "Created At" },
  { key: "last_seen", label: "Last Seen" },
  { key: "wallets_balance", label: "Wallets Balance" },
  { key: "service_spent", label: "Service Spent" },
  { key: "transactions_count", label: "Transactions" },
];

// Optional columns
const optionalColumns = [
  { key: "carrier_id", label: "Carrier ID" },
  { key: "registration_ip", label: "Registration IP" },
  { key: "login_ip", label: "Login IP" },
];

// Sort options
const sortOptions = [
  { value: "created_at", label: "Created At" },
  { value: "email", label: "Email" },
  { value: "username", label: "Username" },
  { value: "wallets_balance", label: "Wallets Balance" },
  { value: "service_spent", label: "Service Spent" },
  { value: "transactions_count", label: "Transactions" },
];

// Filter definitions
const filterDefinitions = [
  { key: "email", label: "Email", type: "text" },
  { key: "username", label: "Username", type: "text" },
  { key: "carrier_id", label: "Carrier ID", type: "text" },
  {
    key: "role_name",
    label: "Role",
    type: "select",
    options: [
      { value: "User", label: "User" },
      { value: "Manager", label: "Manager" },
      { value: "Admin", label: "Admin" },
      { value: "Tester", label: "Tester" },
      { value: "Fraud", label: "Fraud" },
      { value: "Blocked", label: "Blocked" },
    ],
  },
  { key: "registration_ip", label: "Registration IP", type: "text" },
  { key: "login_ip", label: "Login IP", type: "text" },
  {
    key: "wallets_balance",
    label: "Wallet Balance",
    type: "minmax",
    inputType: "number",
    minKey: "wallets_balance_min",
    maxKey: "wallets_balance_max",
  },
  {
    key: "service_spent",
    label: "Service Spent",
    type: "minmax",
    inputType: "number",
    minKey: "service_spent_min",
    maxKey: "service_spent_max",
  },
  {
    key: "transactions_count",
    label: "Transactions",
    type: "minmax",
    inputType: "number",
    minKey: "transactions_count_min",
    maxKey: "transactions_count_max",
  },
  {
    key: "last_seen",
    label: "Last Seen",
    type: "date-range",
    minKey: "min_last_seen",
    maxKey: "max_last_seen",
  },
  {
    key: "created_at",
    label: "Created At",
    type: "date-range",
    minKey: "created_at_min",
    maxKey: "created_at_max",
  },
];

// Custom renderers for date and numeric fields
const customRenderers = {
  created_at: (item) => {
    if (!item.created_at) return "-";
    return new Date(item.created_at).toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  },
  last_seen: (item) => {
    if (!item.last_seen) return "-";
    return new Date(item.last_seen).toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  },
  wallets_balance: (item) => `${item.wallets_balance?.toFixed(2) || 0}`,
  service_spent: (item) => `${item.service_spent?.toFixed(2) || 0}`,
};

const AdminUsers = () => {
  const { token } = useContext(AuthContext);

  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [dataVersion, setDataVersion] = useState(0);

  // Row actions
  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Are you sure you want to delete user ${user.email}? This action cannot be undone.`)) {
      try {
        await deleteUser({ email: user.email });
        alert("User deleted successfully!");
        setDataVersion((v) => v + 1);
      } catch (error) {
        console.error("Delete error:", error);
        alert(`Error deleting user: ${error.message}`);
      }
    }
  };

  const handleUserUpdated = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setDataVersion((v) => v + 1);
  };

  const userRowActions = [
    {
      label: "Edit",
      icon: editIcon,
      onClick: handleEditUser,
    },
    {
      label: "Delete",
      icon: deleteIcon,
      onClick: handleDeleteUser,
    },
  ];

  const fetchDataFn = (params) => fetchUsersAdmin(params);

  return (
    <div className="admin-users-page">
      <PageHeader title="Users" description="View, edit and manage all registered users" />

      <DataTable
        tableId="admin-users"
        key={dataVersion}
        fetchDataFunction={fetchDataFn}
        columns={alwaysVisibleColumns}
        optionalColumns={optionalColumns}
        sortOptions={sortOptions}
        filterDefinitions={filterDefinitions}
        defaultSortBy="created_at"
        itemKey="email"
        customRenderers={customRenderers}
        rowActions={userRowActions}
        showCheckboxes={false}
        showFilters={true}
        showPageSize={true}
      />

      <AdminEditUserModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={editingUser}
        token={token}
        roles={availableRoles}
        onUpdated={handleUserUpdated}
      />
    </div>
  );
};

export default AdminUsers;
